/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Product Engineering landing page with journey stepper.
 * Displays the article series as a book-like chapter progression.
 */

import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PreviewIcon from '@mui/icons-material/Preview';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useMemo } from 'react';
import { useThemeContext } from '@/contexts/ThemeContext';
import { getPageColors, getGradient } from '@/theme/colors';
import { prodengManifest, formatProdengDate } from '@/lib/prodengData';
import { createPageMeta, createCollectionJsonLd, PAGE_KEYWORDS } from '@/lib/meta';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/prodeng')({
  component: ProdEngPage,
  head: ({ matches }) => {
    if (matches.some(match => match.id.includes('/prodeng/articles/'))) return {};
    const base = createPageMeta({
      title: 'Product Engineering — Christian Battaglia',
      description: prodengManifest.subtitle,
      path: '/prodeng',
      image: '/og/prodeng.png',
      imageAlt: 'Product Engineering — Building Systems That Learn',
      keywords: PAGE_KEYWORDS.prodeng,
    });
    return {
      ...base,
      scripts: [
        createCollectionJsonLd({
          name: 'Product Engineering — Building Systems That Learn',
          description: prodengManifest.subtitle,
          path: '/prodeng',
          keywords: PAGE_KEYWORDS.prodeng,
        }),
      ],
    };
  },
});

const BOOK_PARTS = [
  { title: 'The Real Constraint', chapters: '1–3', desc: 'Decision-making under uncertainty is the actual problem' },
  { title: 'The Framework', chapters: '4–6', desc: 'Introducing Fast/Good/Well and known vs unknown unknowns' },
  { title: 'How Systems Respond', chapters: '7–9', desc: 'How teams either absorb or fight uncertainty' },
  { title: 'The Human System', chapters: '10–12', desc: 'Incentives, trust, and organizational dynamics' },
  { title: 'Building Systems That Learn', chapters: '13–15', desc: 'Practical application across scales and domains' },
];

function ProdEngPage() {
  const matches = useMatches();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';
  const colors = useMemo(() => getPageColors('/prodeng'), []);
  const gradient = useMemo(() => getGradient(colors, mode), [colors, mode]);
  const articles = prodengManifest.articles;

  const isChildRoute = matches.some((m) => m.id.startsWith('/prodeng/articles/'));
  if (isChildRoute) return <Outlet />;

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          background: gradient,
          py: { xs: 6, md: 12 },
          px: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.04,
            backgroundImage: `repeating-linear-gradient(
              0deg,
              transparent,
              transparent 39px,
              rgba(255,255,255,0.5) 39px,
              rgba(255,255,255,0.5) 40px
            )`,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 4, md: 6 }}
            alignItems="center"
          >
            <Box
              component="img"
              src="/prodeng/series-hero.webp"
              alt="Product Engineering: Where Product, Engineering, & Business Meet"
              sx={{
                width: { xs: '60%', sm: 240, md: 280 },
                maxWidth: 280,
                height: 'auto',
                borderRadius: 3,
                boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                flexShrink: 0,
                order: { xs: -1, md: 0 },
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Chip
                label="Newsletter Series"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontWeight: 600,
                  mb: 3,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
                  lineHeight: 1.1,
                  mb: 2,
                  fontFamily: '"Georgia", "Times New Roman", serif',
                }}
              >
                {prodengManifest.title}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  maxWidth: 560,
                  mb: 3,
                }}
              >
                {prodengManifest.subtitle}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                {articles.length} articles &bull; Christian Battaglia
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Journey Stepper */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontFamily: '"Georgia", "Times New Roman", serif',
          }}
        >
          The Journey
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 6, maxWidth: 500 }}>
          Each article builds on the last. Start from the beginning or jump to what interests you.
        </Typography>

        <Box sx={{ position: 'relative' }}>
          {articles.map((article: any, idx: number) => {
            const isLast = idx === articles.length - 1;
            const isPublished = article.published;
            const isPreview = !isPublished;

            return (
              <Box
                key={article.slug}
                sx={{ display: 'flex', gap: { xs: 2.5, md: 4 }, mb: isLast ? 0 : 5, position: 'relative' }}
              >
                {/* Timeline rail */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                    width: 40,
                  }}
                >
                  {/* Node */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      flexShrink: 0,
                      ...(isPublished
                        ? {
                            background: isDark
                              ? 'linear-gradient(135deg, #d97706, #f59e0b)'
                              : 'linear-gradient(135deg, #b45309, #d97706)',
                            color: '#fff',
                            boxShadow: '0 2px 8px rgba(180,83,9,0.3)',
                          }
                        : {
                            bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                            color: 'text.secondary',
                            border: '2px dashed',
                            borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                          }),
                    }}
                  >
                    {article.articleNumber}
                  </Box>

                  {/* Connector line */}
                  {!isLast && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        mt: 1,
                        bgcolor: isPublished && articles[idx + 1]?.published
                          ? isDark ? '#d97706' : '#b45309'
                          : isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                        opacity: isPublished ? 0.6 : 0.3,
                      }}
                    />
                  )}
                </Box>

                {/* Card */}
                <Box
                  component={Link}
                  to={article.url}
                  onClick={() => analytics.contentClicked('prodeng_article', article.title, article.url)}
                  sx={{
                    flex: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                    display: 'block',
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor: isPublished
                      ? isDark ? 'rgba(217,119,6,0.25)' : 'rgba(180,83,9,0.15)'
                      : isDark ? 'rgba(217,119,6,0.18)' : 'rgba(180,83,9,0.12)',
                    borderStyle: isPreview ? 'dashed' : 'solid',
                    p: { xs: 2.5, md: 3.5 },
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    opacity: isPreview ? 0.82 : 1,
                    '&:hover': {
                      borderColor: isDark ? '#d97706' : '#b45309',
                      bgcolor: isDark ? 'rgba(217,119,6,0.06)' : 'rgba(180,83,9,0.03)',
                      transform: 'translateX(4px)',
                      boxShadow: isDark
                        ? '0 4px 20px rgba(217,119,6,0.15)'
                        : '0 4px 20px rgba(180,83,9,0.1)',
                      opacity: 1,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 1 }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '1rem', md: '1.15rem' },
                          lineHeight: 1.3,
                          fontFamily: '"Georgia", "Times New Roman", serif',
                          mb: 0.5,
                        }}
                      >
                        {article.title}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.secondary',
                        ml: 2,
                        mt: 0.25,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, mb: 1.5, maxWidth: 500 }}
                  >
                    {article.seoDescription}
                  </Typography>

                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    {article.date && (
                      <Typography variant="caption" color="text.secondary">
                        {formatProdengDate(article.date, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {article.readTime}
                    </Typography>
                    {isPublished ? (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                        label="Published"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)',
                          color: isDark ? '#4ade80' : '#16a34a',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<PreviewIcon sx={{ fontSize: '14px !important' }} />}
                        label="Preview"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: isDark ? 'rgba(217,119,6,0.16)' : 'rgba(180,83,9,0.08)',
                          color: isDark ? '#fbbf24' : '#b45309',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    )}
                    {article.linkedinUrl && (
                      <Chip
                        icon={<LinkedInIcon sx={{ fontSize: '14px !important' }} />}
                        label="LinkedIn"
                        size="small"
                        clickable
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          analytics.outboundLinkClicked(article.linkedinUrl, 'prodeng_index');
                          window.open(article.linkedinUrl, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: isDark ? 'rgba(10,102,194,0.15)' : 'rgba(10,102,194,0.08)',
                          color: isDark ? '#60a5fa' : '#0a66c2',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>

      {/* Book outline / road ahead */}
      <Box
        sx={{
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          borderTop: '1px solid',
          borderColor: 'divider',
          py: { xs: 6, md: 8 },
          px: 3,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontFamily: '"Georgia", "Times New Roman", serif',
            }}
          >
            The Road Ahead
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 5, maxWidth: 500 }}>
            This series is the foundation for a larger work. Here's where it's heading.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {BOOK_PARTS.map((part, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: isDark ? '#fbbf24' : '#b45309',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                  }}
                >
                  Part {idx + 1} &middot; Chapters {part.chapters}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontFamily: '"Georgia", "Times New Roman", serif',
                    lineHeight: 1.3,
                    mt: 0.5,
                    mb: 0.75,
                  }}
                >
                  {part.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {part.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              "How wrong can we afford to be — and how fast can we recover?"
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
